<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from "vue";
import { getArtworkImageSrc } from "~/utils";
import ImageThatFadesIn from "../ui/ImageThatFadesIn.vue";
import HeartLikeButton from "../atoms/HeartLikeButton.vue";
import { type ArtShowingEntry } from "~/types/art-feed.types";
import SelectionRemovalConfirmationModal from "../molecules/SelectionRemovalConfirmationModal.vue";
import { useRemovalConfirmation } from "~/composables/useRemovalConfirmation";
import { useArtBrowsingNudge } from "~/composables/useArtBrowsingNudge";
import { useAnalytics } from "~/composables/useAnalytics";
import { useImpressionTracking } from "~/composables/useImpressionTracking";
import { UNAVAILABLE_ARTWORK_STATUSES } from "~/constants";

const props = defineProps<{
  artwork: ArtShowingEntry;
  hideArtistName?: boolean;
  listContext?: string;
  section?: string;
  position?: number;
  filters?: Record<string, any>;
  sortBy?: string;
  exposureId?: string;
}>();

const appState = useAppState();
const { trackCardClick, trackArtworkSaved, generateExposureId } = useAnalytics();
const { observeCard } = useImpressionTracking();
const { formatDimensions } = useMeasurementUnit();

const cardRef = ref<HTMLElement | null>(null);
const currentExposureId = ref<string>(props.exposureId || "");

const setupImpression = () => {
  if (cardRef.value && props.artwork?.id) {
    observeCard(
      cardRef.value,
      {
        entity_type: "artwork",
        entity_id: props.artwork.id,
        entity_slug: props.artwork.slug,
        list_context: props.listContext || "artworks_catalogue",
        section: props.section,
        position: props.position,
        filters: props.filters,
        sort: props.sortBy,
      },
      (expId) => {
        currentExposureId.value = expId;
      }
    );
  }
};

onMounted(() => {
  setupImpression();
});

watch(cardRef, () => {
  setupImpression();
});

const { toggleSavingArtwork } = useSelectionActions();
const { open, confirmRemoval, onConfirm, onCancel } = useRemovalConfirmation();
const { recordInteraction } = useArtBrowsingNudge();
const { handleMouseMove, handleMouseLeave } = useCardTilt();
const { startArtworkTransition, prepareArtworkTransition, preloadArtworkHiRes, getTransitionStyle, isArtworkActive } = useArtworkViewTransition();

const handleToggle = async () => {
  const isSaved = appState.localSavedItems.value.has(props.artwork.id ?? "");
  trackArtworkSaved({
    artwork_id: props.artwork.id,
    artwork_slug: props.artwork.slug,
    list_context: props.listContext || "artworks_catalogue",
    exposure_id: currentExposureId.value || undefined,
    detail_or_card: "card",
    action: isSaved ? "unsaved" : "saved",
  });
  await toggleSavingArtwork(props.artwork.id, confirmRemoval);
};

const handleCardClick = async (event: MouseEvent) => {
  recordInteraction();

  const targetUrl = `/artworks/${props.artwork.slug ?? props.artwork.id}`;
  const effectiveExposureId = currentExposureId.value || generateExposureId();
  currentExposureId.value = effectiveExposureId;

  trackCardClick({
    entity_type: "artwork",
    entity_id: props.artwork.id,
    entity_slug: props.artwork.slug,
    list_context: props.listContext || "artworks_catalogue",
    section: props.section,
    position: props.position,
    exposure_id: effectiveExposureId,
    filters: props.filters,
    sort: props.sortBy,
    destination_path: targetUrl,
    click_target: "artwork_card",
  });

  // Allow native browser behavior for new tab / modifier clicks
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) {
    return;
  }

  event.preventDefault();
  await startArtworkTransition(props.artwork, () => {
    return navigateTo(targetUrl);
  });
};

const handleMouseEnter = () => {
  preloadArtworkHiRes(props.artwork.photo_names?.[0]);
  preloadRouteComponents(`/artworks/${props.artwork.slug ?? props.artwork.id}`);
};
</script>

<template>
  <NuxtLink
    ref="cardRef"
    :to="`/artworks/${artwork.slug ?? artwork.id}`"
    class="group card-tilt-container flex flex-col custom-card"
    @mousemove="handleMouseMove"
    @mouseleave="handleMouseLeave"
    @mouseenter="handleMouseEnter"
    @pointerdown="prepareArtworkTransition(artwork)"
    @click="handleCardClick"
  >
    <div class="relative block overflow-hidden">
      <ImageThatFadesIn
        class="w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        :class="{ 'artwork-transition-target': isArtworkActive(artwork) }"
        :style="getTransitionStyle(artwork)"
        :src="getArtworkImageSrc(artwork.photo_names?.[0] ?? '', 360)"
        :width="artwork.photo_widths?.[0]"
        :height="artwork.photo_heights?.[0]"
        :alt="`${artwork.name} by ${artwork.artist_name}`"
      />

      <!-- Multi-photo count chip — bottom right -->
      <div
        v-if="artwork.photo_names && artwork.photo_names.length > 1"
        class="absolute bottom-2 right-2 z-10 flex items-center gap-1.5 pointer-events-none"
      >
        <div
          class="flex items-center gap-1 rounded-sm bg-black/60 px-2 py-0.5 text-xs font-semibold text-white backdrop-blur-sm"
        >
          <Icon name="material-symbols:photo-library-outline" class="size-3" />
          {{ artwork.photo_names.length }}
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-1 md:gap-2 px-3 pt-[14px] pb-3">
      <div
        class="flex items-start justify-between gap-1 text-base font-bold text-on-primary-heading md:text-lg"
      >
        <h3 class="m-0">{{ artwork.name }}</h3>
        <HeartLikeButton
          :model-value="appState.localSavedItems.value.has(artwork.id ?? '')"
          :size="22"
          @click.stop.prevent
          @toggle="handleToggle"
        />
      </div>
      <div v-if="!$props.hideArtistName" class="flex w-full items-center justify-between gap-4">
        <span class="text-sm font-normal text-on-primary-body md:text-base lg:text-lg truncate">
          {{ artwork.artist_name }}
        </span>
        <UBadge
          v-if="['Sold', 'Gift', 'Reserved'].includes(artwork.availability ?? '')"
          :label="['Sold', 'Gift'].includes(artwork.availability ?? '') ? 'Sold' : 'Reserved'"
          size="md"
          class="rounded-[6px] bg-on-primary-body px-2 py-1 text-xs font-medium text-white shrink-0"
        />
      </div>
      <!-- Size & Price row -->
      <div
        v-if="artwork.display_size || (!UNAVAILABLE_ARTWORK_STATUSES.includes(artwork.availability ?? '') && artwork.listing_price != null)"
        class="flex flex-col md:flex-row md:items-center md:justify-between gap-0.5 md:gap-2 mt-0.5"
      >
        <span v-if="artwork.display_size" class="text-sm text-on-primary-body/75">
          {{ formatDimensions(artwork.display_size) }}
        </span>
        <span v-if="!UNAVAILABLE_ARTWORK_STATUSES.includes(artwork.availability ?? '') && artwork.listing_price != null" class="text-sm font-semibold text-on-primary-body shrink-0">
          ₹{{ artwork.listing_price.toLocaleString("en-IN") }}
        </span>
      </div>

    </div>
  </NuxtLink>

  <SelectionRemovalConfirmationModal
    v-model:open="open"
    @confirm="onConfirm"
    @cancel="onCancel"
  />
</template>

<style scoped>
.custom-card {
  background: #f2f2f2;
  transition: transform 0.15s ease, box-shadow 0.35s ease;
  transform-style: preserve-3d;
  border-radius: 6px;
  overflow: hidden;
  transform: translateZ(0);
}
.custom-card:hover {
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.12);
}
.custom-card > :first-child {
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  overflow: hidden;
  transform: translateZ(0);
}
.custom-card > :last-child {
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
}
</style>
